import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { generarNombreArchivo } from "./nombrePDF";
import { obtenerDatosTenantPDF } from "./tenantPDF";
import { formatearMoneda, linkMaps } from "./internacional";

// ─── Paleta de colores ───────────────────────────────────────────────────────
const PALETTE = {
  // Acento orden (cyan SwAlquiler) — gradiente de oscuro a claro
  cyanDark:   [0, 119, 182],   // #0077B6
  cyanLight:  [0, 180, 216],   // #00B4D8
  cyanBanda:  [220, 237, 247], // #DCEDF7 (banda suave, un poco más cargada)
  // Acento cotización (sage) — gradiente de oscuro a claro
  sageDark:   [108, 122, 103], // #6C7A67
  sageLight:  [156, 170, 148], // #9CAA94
  sageBanda:  [228, 234, 224], // #E4EAE0
  // Neutros
  dark:       [33, 37, 41],
  grayText:   [75, 85, 99],
  grayLabel:  [140, 145, 155],
  hairline:   [229, 231, 235],
};

// ─── Redes sociales ────────────────────────────────────────────────────────
const REDES_PDF = {
  instagram: { bg: "#E1306C", bgArr: [225, 48, 108] },
  facebook:  { bg: "#1877F2", bgArr: [24, 119, 242] },
  tiktok:    { bg: "#010101", bgArr: [1, 1, 1] },
  youtube:   { bg: "#FF0000", bgArr: [255, 0, 0] },
  whatsapp:  { bg: "#25D366", bgArr: [37, 211, 102] },
  web:       { bg: "#0077B6", bgArr: [0, 119, 182] },
};

function generarIconoRed(red, size = 48) {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  const cfg = REDES_PDF[red];
  if (!cfg) return null;

  const r = size * 0.2;
  ctx.fillStyle = cfg.bg;
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.lineTo(size - r, 0);
  ctx.quadraticCurveTo(size, 0, size, r);
  ctx.lineTo(size, size - r);
  ctx.quadraticCurveTo(size, size, size - r, size);
  ctx.lineTo(r, size);
  ctx.quadraticCurveTo(0, size, 0, size - r);
  ctx.lineTo(0, r);
  ctx.quadraticCurveTo(0, 0, r, 0);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = "#FFFFFF";
  ctx.strokeStyle = "#FFFFFF";
  const cx = size / 2;
  const cy = size / 2;
  const u = size / 48;

  if (red === "instagram") {
    ctx.lineWidth = 2.5 * u;
    const boxS = 22 * u;
    const boxR = 6 * u;
    ctx.beginPath();
    ctx.moveTo(cx - boxS/2 + boxR, cy - boxS/2);
    ctx.lineTo(cx + boxS/2 - boxR, cy - boxS/2);
    ctx.quadraticCurveTo(cx + boxS/2, cy - boxS/2, cx + boxS/2, cy - boxS/2 + boxR);
    ctx.lineTo(cx + boxS/2, cy + boxS/2 - boxR);
    ctx.quadraticCurveTo(cx + boxS/2, cy + boxS/2, cx + boxS/2 - boxR, cy + boxS/2);
    ctx.lineTo(cx - boxS/2 + boxR, cy + boxS/2);
    ctx.quadraticCurveTo(cx - boxS/2, cy + boxS/2, cx - boxS/2, cy + boxS/2 - boxR);
    ctx.lineTo(cx - boxS/2, cy - boxS/2 + boxR);
    ctx.quadraticCurveTo(cx - boxS/2, cy - boxS/2, cx - boxS/2 + boxR, cy - boxS/2);
    ctx.closePath();
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, cy, 7 * u, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx + 8 * u, cy - 8 * u, 2 * u, 0, Math.PI * 2);
    ctx.fill();
  } else if (red === "facebook") {
    ctx.font = `bold ${28 * u}px Arial`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("f", cx + 1 * u, cy + 2 * u);
  } else if (red === "tiktok") {
    ctx.font = `bold ${26 * u}px Arial`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("♪", cx, cy + 1 * u);
  } else if (red === "youtube") {
    ctx.beginPath();
    ctx.moveTo(cx - 6 * u, cy - 9 * u);
    ctx.lineTo(cx - 6 * u, cy + 9 * u);
    ctx.lineTo(cx + 10 * u, cy);
    ctx.closePath();
    ctx.fill();
  } else if (red === "whatsapp") {
    ctx.font = `${22 * u}px Arial`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("✆", cx, cy + 1 * u);
  } else if (red === "web") {
    ctx.lineWidth = 2 * u;
    ctx.beginPath();
    ctx.arc(cx, cy, 10 * u, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx - 10 * u, cy);
    ctx.lineTo(cx + 10 * u, cy);
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(cx, cy, 5 * u, 10 * u, 0, 0, Math.PI * 2);
    ctx.stroke();
  }
  return canvas.toDataURL("image/png");
}

function tema(tipo) {
  return tipo === "cotizacion"
    ? { dark: PALETTE.sageDark, light: PALETTE.sageLight, banda: PALETTE.sageBanda }
    : { dark: PALETTE.cyanDark, light: PALETTE.cyanLight, banda: PALETTE.cyanBanda };
}

// ─── Helpers ────────────────────────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toLocaleString("es-CO");
const num = (n, def = 0) => {
  const v = Number(n);
  return Number.isFinite(v) ? v : def;
};
const soloFecha = (f) => {
  if (!f) return "-";
  if (f instanceof Date) {
    const dd = String(f.getDate()).padStart(2, "0");
    const mm = String(f.getMonth() + 1).padStart(2, "0");
    const yy = f.getFullYear();
    return `${dd}/${mm}/${yy}`;
  }
  const s = String(f).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) return `${dmy[1].padStart(2, "0")}/${dmy[2].padStart(2, "0")}/${dmy[3]}`;
  const d = new Date(s);
  if (!isNaN(d)) {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()}`;
  }
  return "-";
};

const procesarImagen = (src, width = 150, calidad = 1.0) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const escala = width / img.width;
      canvas.width = width;
      canvas.height = img.height * escala;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/png", calidad));
    };
    img.src = src;
  });

export async function generarPDF(documento, tipo = "cotizacion") {
  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const emp = await obtenerDatosTenantPDF();
  const logo = emp.logoUrl ? await procesarImagen(emp.logoUrl, 250, 1.0) : null;
  const fondo = emp.fondoUrl ? await procesarImagen(emp.fondoUrl, 300, 0.9) : null;

  const insertarFondo = () => {
    if (!fondo) return;
    const centerX = (pageW - 150) / 2;
    const centerY = (pageH - 150) / 2;
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity: 0.06 }));
    doc.addImage(fondo, "PNG", centerX, centerY, 150, 150);
    doc.restoreGraphicsState();
  };
  insertarFondo();

  const T = tema(tipo);

  // ═════════════════════════════════════════════════════════════════════════
  // MÁRGENES Y BANDA (MODIFICADO: Margen Superior)
  // ═════════════════════════════════════════════════════════════════════════
  const MARGIN_X = 14; 
  const rightMarginX = pageW - MARGIN_X;
  const MARGIN_TOP = 10; // Margen superior blanco
  
  const bandaH = 26; 
  
  // Banda desplazada hacia abajo
  doc.setFillColor(...T.banda);
  doc.rect(0, MARGIN_TOP, pageW, bandaH, "F");
  doc.setFillColor(...T.dark);
  doc.rect(0, MARGIN_TOP, pageW, 1.2, "F");

  // ═════════════════════════════════════════════════════════════════════════
  // ENCABEZADO
  // ═════════════════════════════════════════════════════════════════════════
  if (logo) {
    const logoSize = 32; 
    const logoX = MARGIN_X;
    const logoY = MARGIN_TOP + 4; // Ajustado al nuevo margen

    doc.setFillColor(255, 255, 255);
    doc.circle(logoX + logoSize / 2, logoY + logoSize / 2, (logoSize / 2) + 1.5, "F");
    doc.addImage(logo, "PNG", logoX, logoY, logoSize, logoSize);
  }

  const infoX = MARGIN_X + 38; 
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14); 
  doc.setTextColor(...PALETTE.dark);
  doc.text(emp.nombre || "", infoX, MARGIN_TOP + 11);
  
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5); 
  doc.setTextColor(...PALETTE.grayText);
  doc.text(emp.direccion || "", infoX, MARGIN_TOP + 16);
  doc.text(emp.telefono ? `Cel-Whatsapp ${emp.telefono}` : "", infoX, MARGIN_TOP + 20.5);

  // ─── BLOQUE DERECHO (MODIFICADO: Sin número de orden) ───
  const tipoTexto = tipo === "cotizacion" ? "COTIZACIÓN" : "ORDEN DE PEDIDO";
  const fechaCreacion = soloFecha(documento.fecha_creacion);
  
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11.5); 
  doc.setTextColor(100, 110, 120); 
  doc.text(tipoTexto, rightMarginX, MARGIN_TOP + 13.5, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10); 
  doc.setTextColor(...PALETTE.grayText);
  doc.text(`Fecha: ${fechaCreacion}`, rightMarginX, MARGIN_TOP + 18.5, { align: "right" }); 

  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0);

  // ═════════════════════════════════════════════════════════════════════════
  // FECHAS DEL EVENTO (Guion seguro sin símbolos raros, misma fuente)
  // ═════════════════════════════════════════════════════════════════════════
  const esMultiDias = !!documento.multi_dias;
  const listaFechas = Array.isArray(documento.fechas_evento)
    ? [...documento.fechas_evento].filter(Boolean).sort()
    : [];

  let valorFechaEvento = "-";
  if (!esMultiDias) {
    const unica = documento.fecha_evento || listaFechas[0] || null;
    valorFechaEvento = unica ? soloFecha(unica) : "-";
  } else {
    const primera = listaFechas[0] || documento.fecha_evento || null;
    const ultima = listaFechas.length > 1
      ? listaFechas[listaFechas.length - 1]
      : (listaFechas[0] || documento.fecha_evento || null);
    const ini = primera ? soloFecha(primera) : "-";
    const fin = ultima ? soloFecha(ultima) : "-";
    // Usamos guion normal (-) en vez del largo (—) que causa símbolos raros
    valorFechaEvento = (ini !== fin) ? `${ini} - ${fin}` : ini;
  }

  // ═════════════════════════════════════════════════════════════════════════
  // DATOS DEL CLIENTE
  // ═════════════════════════════════════════════════════════════════════════
  const renderField = (label, valor, x, y, maxW) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...PALETTE.grayLabel);
    doc.text(String(label).toUpperCase(), x, y);
    doc.setFontSize(10);
    doc.setTextColor(...PALETTE.dark);
    const textLines = doc.splitTextToSize(String(valor || "—"), maxW);
    doc.text(textLines[0], x, y + 4.2);
    return { x, y: y + 4.2, w: doc.getTextWidth(textLines[0]) };
  };

  const yData = MARGIN_TOP + 42;
  const colLeftX = MARGIN_X;
  const colRightX = 125; // ─── MOVÍDO a la derecha ───
  const colW = 75; 
  const fieldGap = 9;        

  const filasIzq = [
    ["Cliente", documento.nombre_cliente || "Cliente"],
    ["Identificación", documento.identificacion || "N/A"],
    ["Teléfono", documento.telefono || "N/A"],
  ];
  const filasDer = [
    ["Correo", documento.email || "N/A"],
    ["Dirección", documento.direccion || "N/A"],
    [esMultiDias ? "Fechas del evento" : "Fecha del evento", valorFechaEvento],
  ];

  filasIzq.forEach((f, i) =>
    renderField(f[0], f[1], colLeftX, yData + i * fieldGap, colW)
  );
  filasDer.forEach((f, i) =>
    renderField(f[0], f[1], colRightX, yData + i * fieldGap, colW)
  );

  // ─── Teléfono clickeable: tocar el número abre WhatsApp ──────────────
  if (documento.telefono) {
    const yTelBase = yData + 2 * fieldGap + 4.2;
    doc.setFontSize(10);
    const telW = doc.getTextWidth(String(documento.telefono));
    const digits = String(documento.telefono).replace(/\D/g, "");
    const numConPais = digits.length === 10 ? "57" + digits : digits;

    doc.link(colLeftX, yTelBase - 4, telW + 1, 5, {
      url: `https://wa.me/${numConPais}`,
    });
  }

  const yAfterCols = yData + 3 * fieldGap;

  doc.setDrawColor(...PALETTE.hairline);
  doc.setLineWidth(0.2);
  doc.line(MARGIN_X, yAfterCols, rightMarginX, yAfterCols);

  const yEvtRow = yAfterCols + 4;
  const hayLugar = !!(documento.lugar_evento || documento.ubicacion_maps);
  const hayTipo = !!documento.tipo_evento;

  if (hayTipo) {
    renderField("Tipo de evento", documento.tipo_evento, colLeftX, yEvtRow, colW);
  }
  if (hayLugar) {
    const r = renderField("Lugar del evento", documento.lugar_evento || "Ver ubicación en el mapa", colRightX, yEvtRow, colW);
    const mapsUrl = linkMaps(documento.ubicacion_maps || documento.lugar_evento, emp);
    doc.link(r.x, r.y - 3.5, r.w, 5, { url: mapsUrl });
  }

  const yPostEvtRow = (hayLugar || hayTipo) ? yEvtRow + fieldGap : yAfterCols + 2;
  const startYTabla = yPostEvtRow + 3;

  // ═════════════════════════════════════════════════════════════════════════
  // TABLA DE PRODUCTOS (MODIFICADO: Lógica de V. x Días)
  // ═════════════════════════════════════════════════════════════════════════
  const esMulti = Boolean(documento.multi_dias);
  const nd = num(documento.numero_dias, 1);
  const mostrarNotas = Boolean(documento.mostrar_notas);

  const head = (() => {
    if (esMulti && mostrarNotas) return [["Cantidad", "Artículo", "Notas", "Precio Unit.", "V. x Días", "Subtotal"]];
    if (esMulti && !mostrarNotas) return [["Cantidad", "Artículo", "Precio Unit.", "V. x Días", "Subtotal"]];
    if (!esMulti && mostrarNotas) return [["Cantidad", "Artículo", "Notas", "Precio", "Subtotal"]];
    return [["Cantidad", "Artículo", "Precio", "Subtotal"]];
  })();

  const body = (documento.productos || []).map((p) => {
    const cantidad = num(p.cantidad);
    const precio = num(p.precio);
    // V. x Días ya no muestra el '3', sino el resultado de precio * dias (ej. 1000 * 3 = 3000)
    // Si el producto dice multiplicarPorDias = false (ej. transporte), el valor se queda igual (1000 * 1 = 1000)
    const valorPorDias = precio * (esMulti ? (p.multiplicarPorDias === false ? 1 : nd) : 1);
    const sub = num(p.subtotal, valorPorDias * cantidad);
    
    const base = [String(cantidad), p.descripcion || p.nombre || "", formatearMoneda(precio, emp), formatearMoneda(sub, emp)];
    
    // Si es multidiario, insertamos el nuevo valor calculado de 'V. x Días' en medio
    if (esMulti) {
      base.splice(3, 0, formatearMoneda(valorPorDias, emp));
    }
    if (mostrarNotas) base.splice(2, 0, p.notas || "");
    return base;
  });

  const columnStyles = (() => {
    if (esMulti && mostrarNotas) {
      return {
        0: { cellWidth: 20, halign: "center" },
        1: { cellWidth: 52 }, 
        2: { cellWidth: 30 },
        3: { cellWidth: 26, halign: "center" },
        4: { cellWidth: 26, halign: "center" },
        5: { cellWidth: 28, halign: "center" },
      };
    } else if (esMulti && !mostrarNotas) {
      return {
        0: { cellWidth: 20, halign: "center" },
        1: { cellWidth: 70 },
        2: { cellWidth: 30, halign: "center" },
        3: { cellWidth: 30, halign: "center" },
        4: { cellWidth: 32, halign: "center" },
      };
    } else if (!esMulti && mostrarNotas) {
      return {
        0: { cellWidth: 22, halign: "center" },
        1: { cellWidth: 66 },
        2: { cellWidth: 38 },
        3: { cellWidth: 28, halign: "center" },
        4: { cellWidth: 28, halign: "center" },
      };
    } else {
      return {
        0: { cellWidth: 22, halign: "center" },
        1: { cellWidth: 100 },
        2: { cellWidth: 30, halign: "center" },
        3: { cellWidth: 30, halign: "center" },
      };
    }
  })();

  const tableLeftX = MARGIN_X;
  const tableRightX = rightMarginX;
  const tableW = tableRightX - tableLeftX;

  autoTable(doc, {
    head,
    body,
    startY: startYTabla,
    styles: { font: "helvetica", fontSize: 9.5, cellPadding: 1.5, textColor: PALETTE.dark },
    headStyles: {
      fillColor: false,
      textColor: 255,
      halign: "center",
      valign: "middle",
      fontStyle: "bold",
      fontSize: 9.5,
      cellPadding: { top: 3, bottom: 3, left: 2.5, right: 2.5 },
      minCellHeight: 9,
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles,
    margin: { left: MARGIN_X, right: MARGIN_X }, 
    willDrawCell: (data) => {
      if (data.section === "head") {
        const { x, y, width, height } = data.cell;
        const steps = Math.max(20, Math.ceil(width));
        const stripW = width / steps;
        for (let i = 0; i < steps; i++) {
          const localX = i * stripW;
          const globalX = x + localX - tableLeftX;
          const t = Math.max(0, Math.min(1, globalX / tableW));
          const r = Math.round(T.dark[0] + t * (T.light[0] - T.dark[0]));
          const g = Math.round(T.dark[1] + t * (T.light[1] - T.dark[1]));
          const b = Math.round(T.dark[2] + t * (T.light[2] - T.dark[2]));
          doc.setFillColor(r, g, b);
          doc.rect(x + localX, y, stripW + 0.2, height, "F");
        }
      }
    },
    didDrawPage: insertarFondo,
  });

  let y = (doc.lastAutoTable?.finalY || 100) + 16; 

  const bottomMargin = 15;
  const safeBottom = pageH - bottomMargin;

  // ═══ NOTAS DEL DOCUMENTO (debajo de la tabla, antes de los totales) ═══
  const notasTexto = (documento.notas_documento || "").trim();
  if (notasTexto) {
    const altoLinea = 5;
    doc.setFontSize(10.5);
    const lineasNotas = doc.splitTextToSize(notasTexto, pageW - (MARGIN_X * 2));

    // Si no cabe ni el título + una línea, pasar a nueva página
    if (y + 8 + altoLinea > safeBottom) {
      doc.addPage();
      insertarFondo();
      y = MARGIN_TOP + 15;
    }

    // Título
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...PALETTE.grayLabel);
    doc.text("NOTAS", MARGIN_X, y);
    doc.setDrawColor(...PALETTE.hairline);
    doc.setLineWidth(0.3);
    doc.line(MARGIN_X, y + 2, MARGIN_X + 22, y + 2);
    y += 8;

    // Texto (con salto de página línea a línea si es largo)
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.5);
    doc.setTextColor(...PALETTE.grayText);
    lineasNotas.forEach((linea) => {
      if (y + altoLinea > safeBottom) {
        doc.addPage();
        insertarFondo();
        y = MARGIN_TOP + 15;
      }
      doc.text(linea, MARGIN_X, y);
      y += altoLinea;
    });

    y += 6; // separación antes de los totales
  }

  const totalBruto = (documento.productos || []).reduce(
    (acc, p) =>
      acc + num(p.subtotal, num(p.precio) * num(p.cantidad) * (esMulti ? (p.multiplicarPorDias === false ? 1 : nd) : 1)),
    0
  );
  const descuento = num(documento.descuento, 0);
  const retencion = num(documento.retencion, 0);
  const totalNeto = num(documento.total_neto, Math.max(0, totalBruto - descuento - retencion));
  // 🚫 Una cotización nunca muestra abonos en el PDF, aunque el documento los
  // tenga guardados. Si después se convierte en pedido, ahí sí aparecen.
  // ✅ Un pedido puede tener abonos en DOS campos: los del documento (abonos)
  // y los cobrados el día del evento en Recepción (abonos_recepcion). Los dos
  // son plata que el cliente ya pagó, así que los dos van en el PDF.
  const esCotizacion = tipo === "cotizacion";
  const abonosDoc =
    esCotizacion || !Array.isArray(documento.abonos) ? [] : documento.abonos;
  const abonosRec =
    esCotizacion || !Array.isArray(documento.abonos_recepcion)
      ? []
      : documento.abonos_recepcion;
  // Ordenados por fecha para que la numeración del PDF quede cronológica.
  const abonosArr = [...abonosDoc, ...abonosRec].sort((a, b) => {
    const fa = String((typeof a === "object" ? a.fecha : "") || "");
    const fb = String((typeof b === "object" ? b.fecha : "") || "");
    return fa.localeCompare(fb);
  });
  const totalAbonos = abonosArr.reduce((acc, a) => acc + num(typeof a === "object" ? a.valor : a, 0), 0);
  const saldo = Math.max(0, totalNeto - totalAbonos);
  const flagDesc = (Boolean(documento.aplicar_descuento) || descuento > 0) && descuento > 0;
  const flagRet  = (Boolean(documento.aplicar_retencion) || retencion > 0) && retencion > 0;
  const garantia = num(documento.garantia, 0);

  let lineasTot = 1;
  if (flagDesc) lineasTot++;
  if (flagRet) lineasTot++;
  if (flagDesc || flagRet) lineasTot++;
  lineasTot += abonosArr.length;
  lineasTot++;
  const altoTotales = lineasTot * 7.5 + 8;

  if (y + altoTotales > safeBottom) {
    doc.addPage();
    insertarFondo();
    y = MARGIN_TOP + 15;
  }

  const totLabelX = rightMarginX - 65; 
  const totValueX = rightMarginX;
  let yTot = y;

  if (garantia > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11.5); 
    doc.setTextColor(...PALETTE.dark);
    doc.text("Garantía:", MARGIN_X, yTot);
    
    const lblWidth = doc.getTextWidth("Garantía: ");
    doc.setFont("helvetica", "normal");
    doc.text(formatearMoneda(garantia, emp), MARGIN_X + lblWidth + 2, yTot);

    if (documento.fecha_garantia) {
      doc.setFontSize(10.5);
      doc.setTextColor(...PALETTE.grayText);
      doc.text(`Recibida: ${soloFecha(documento.fecha_garantia)}`, MARGIN_X, yTot + 6.5);
    }
  }

  const renderTotalLine = (label, valor, opts = {}) => {
    if (opts.divider) {
      doc.setDrawColor(...PALETTE.hairline);
      doc.setLineWidth(0.3);
      doc.line(totLabelX, yTot - 2.5, totValueX, yTot - 2.5);
      yTot += 2.5;
    }
    doc.setFont("helvetica", opts.bold ? "bold" : "normal");
    doc.setFontSize(opts.size || 11.5); 
    doc.setTextColor(...(opts.muted ? PALETTE.grayText : PALETTE.dark));
    doc.text(label, totLabelX, yTot);
    doc.text(valor, totValueX, yTot, { align: "right" });
    yTot += opts.gap || 7.5; 
  };

  renderTotalLine("Subtotal", formatearMoneda(totalBruto, emp));
  if (flagDesc) renderTotalLine("Descuento", `-${formatearMoneda(descuento, emp)}`, { muted: true });
  if (flagRet)  renderTotalLine("Retención", `-${formatearMoneda(retencion, emp)}`, { muted: true });
  if (flagDesc || flagRet) renderTotalLine("Total", formatearMoneda(totalNeto, emp), { bold: true, divider: true });

  if (abonosArr.length > 0) {
    abonosArr.forEach((a, i) => {
      const valor = typeof a === "object" ? a.valor : a;
      const fechaRaw = typeof a === "object" ? a.fecha : "";
      const fecha = fechaRaw ? soloFecha(fechaRaw) : "";
      const label = fecha ? `Abono ${i + 1} (${fecha})` : `Abono ${i + 1}`;
      renderTotalLine(label, `-${formatearMoneda(num(valor), emp)}`, { muted: true });
    });
  }

  renderTotalLine("SALDO FINAL", formatearMoneda(saldo, emp), { bold: true, size: 14, divider: true, gap: 8 });

  // ═════════════════════════════════════════════════════════════════════════
  // TÉRMINOS Y CONDICIONES Y REDES
  // ═════════════════════════════════════════════════════════════════════════
  y = yTot + 12; 

  let altoTerminos = 16; 
  if (documento.fecha_entrega || documento.fecha_devolucion) altoTerminos += 6.5;
  
  let lineasCond = [];
  if (emp.textoCondicionesPdf) {
    doc.setFontSize(10); 
    lineasCond = doc.splitTextToSize(emp.textoCondicionesPdf, pageW - (MARGIN_X * 2));
    altoTerminos += lineasCond.length * 5 + 4;
  }
  const altoRedes = 25; 
  const altoBloqueCentrado = altoTerminos + altoRedes;

  if (y + altoBloqueCentrado + 5 > safeBottom) {
    doc.addPage();
    insertarFondo();
    y = (pageH - altoBloqueCentrado) / 2; 
  } else {
    const spaceLeft = safeBottom - y - altoBloqueCentrado;
    if (spaceLeft > 15) {
      y += spaceLeft / 2.5; 
    }
  }

  let yT = y;
  const centerX = pageW / 2;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11.5); 
  doc.setTextColor(...PALETTE.grayLabel);
  doc.text("TÉRMINOS Y CONDICIONES", centerX, yT, { align: "center" });

  doc.setDrawColor(...PALETTE.hairline);
  doc.setLineWidth(0.3);
  doc.line(centerX - 45, yT + 2, centerX + 45, yT + 2);
  yT += 9;

  doc.setFontSize(10.5); 
  let datesText = "";
  if (documento.fecha_entrega) datesText += `Entrega: ${soloFecha(documento.fecha_entrega)}`;
  if (documento.fecha_devolucion) {
      if (datesText) datesText += `   |   `;
      datesText += `Devolución: ${soloFecha(documento.fecha_devolucion)}`;
  }

  if (datesText) {
      doc.setTextColor(...PALETTE.grayText);
      doc.text(datesText, centerX, yT, { align: "center" });
      yT += 6.5;
  }

  if (emp.textoCondicionesPdf) {
      yT += 2;
      doc.setFontSize(10); 
      doc.setTextColor(...PALETTE.grayText);
      lineasCond.forEach((linea, i) => {
        doc.text(linea, centerX, yT + i * 5, { align: "center" });
      });
      yT += lineasCond.length * 5;
  }

  yT += 6; 

  doc.setDrawColor(...PALETTE.hairline);
  doc.setLineWidth(0.3);
  doc.line(centerX - 60, yT, centerX + 60, yT); 
  yT += 8;

  const redes = (emp.redesSociales || []).filter(r => r.usuario && REDES_PDF[r.red]);
  if (redes.length > 0) {
    const iconSize = 5.5;
    const gap = 8;
    const redesConIcono = redes.map((red) => {
      const icono = generarIconoRed(red.red, 48);
      const texto = red.usuario;
      const textoW = doc.getStringUnitWidth(texto) * 9.5 / doc.internal.scaleFactor;
      const pillW = iconSize + 2 + textoW;
      return { red, icono, texto, textoW, pillW };
    });
    const totalWidth = redesConIcono.reduce((acc, r) => acc + r.pillW, 0) + (redes.length - 1) * gap;
    let xPill = (pageW - totalWidth) / 2;

    // Helper para construir URL de cada red social
    const buildRedURL = (redId, usuario) => {
      const u = String(usuario || "").trim().replace(/^@/, "");
      if (!u) return null;
      switch (redId) {
        case "instagram": return `https://instagram.com/${u}`;
        case "facebook":  return u.startsWith("facebook.com") ? `https://${u}` : `https://facebook.com/${u}`;
        case "tiktok":    return `https://tiktok.com/@${u}`;
        case "youtube":   return u.startsWith("youtube.com") ? `https://${u}` : `https://youtube.com/@${u}`;
        case "whatsapp": {
          const digits = u.replace(/\D/g, "");
          const numConPais = digits.length === 10 ? "57" + digits : digits;
          return `https://wa.me/${numConPais}`;
        }
        default: return null;
      }
    };

    redesConIcono.forEach(({ red, icono, texto, pillW }) => {
      if (icono) {
        try { doc.addImage(icono, "PNG", xPill, yT - 4, iconSize, iconSize); } catch (e) {}
      }
      doc.setFontSize(9.5);
      doc.setTextColor(...PALETTE.grayText);
      doc.text(texto, xPill + iconSize + 2, yT);

      // Link sobre todo el pill (ícono + texto)
      const url = buildRedURL(red.red, red.usuario);
      if (url) {
        doc.link(xPill, yT - 4, pillW, iconSize + 2, { url });
      }

      xPill += pillW + gap;
    });
  } else if (emp.email) {
    doc.setFontSize(9);
    doc.setTextColor(...PALETTE.grayText);
    doc.text(emp.email, centerX, yT, { align: "center" });
  }

  doc.setTextColor(0, 0, 0);

  // ─── Guardar PDF ─────────────────────────────────────────────────────────
  const fechaSegura = documento.fecha_creacion || new Date();
  const nombreArchivo = generarNombreArchivo(tipo, fechaSegura, documento.nombre_cliente);
  doc.save(nombreArchivo);
}